import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import { Address } from "@/services/backend/addresses/types/address";
import { FieldErrors } from "@/shared/lib/errors/validation-error";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { googleMapsSearchUrl } from "@/services/backend/addresses/lib/maps-url";

type Mutation<Req, Res> = () => RtkQueryMutation<Req, Res>;

interface ServiceAddressCardProps {
  address: Address;
  onAddressChange: (serviceAddress: Address) => void;
  title?: string;
  description?: string;
  /**
   * Additional errors, keyed relative to the address (e.g. "street"),
   * merged with the errors of the ErrValidationFailedContext.
   */
  fieldErrors?: FieldErrors;
  /**
   * The field name of the address in the ErrValidationFailedContext. Its fields
   * are read from and reset at `${fieldName}.<field>`, e.g.
   * "serviceAddress.street".
   */
  fieldName?: string;
  disabled?: boolean;
  hideNavigationButton?: boolean;
}

export function ServiceAddressCard({
  address,
  onAddressChange,
  title = t("Serviceadresse"),
  description = t("Adresse an der der Service durchgeführt werden soll."),
  fieldErrors = undefined,
  fieldName = "serviceAddress",
  disabled = false,
  hideNavigationButton = false,
}: ServiceAddressCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {!hideNavigationButton && (
          <a
            href={googleMapsSearchUrl(address)}
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="outline">{t("In Karte öffnen")}</Button>
          </a>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <TextareaField
          label={t("Kommentar")}
          value={address.comment}
          onChange={(e) =>
            onAddressChange({ ...address, comment: e.target.value })
          }
          fieldName={joinFieldName(fieldName, "comment")}
          errors={fieldErrors?.comment}
          disabled={disabled}
        />
        <TextInputField
          label={t("Firma")}
          text={address.company}
          onTextChange={(company) => onAddressChange({ ...address, company })}
          fieldName={joinFieldName(fieldName, "company")}
          errors={fieldErrors?.company}
          disabled={disabled}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextInputField
            label={t("Straße & Hausnummer")}
            text={address.street}
            onTextChange={(street) => onAddressChange({ ...address, street })}
            fieldName={joinFieldName(fieldName, "street")}
            errors={fieldErrors?.street}
            disabled={disabled}
          />
          <div className="grid grid-cols-4 gap-4">
            <TextInputField
              label={t("PLZ")}
              text={address.postalCode}
              onTextChange={(postalCode) =>
                onAddressChange({ ...address, postalCode })
              }
              fieldName={joinFieldName(fieldName, "postalCode")}
              errors={fieldErrors?.postalCode}
              disabled={disabled}
            />
            <TextInputField
              label={t("Stadt")}
              text={address.city}
              onTextChange={(city) => onAddressChange({ ...address, city })}
              fieldName={joinFieldName(fieldName, "city")}
              errors={fieldErrors?.city}
              disabled={disabled}
              fieldProps={{ className: "col-span-3" }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ServiceAddressCardWithMutationProps<Req, Res> {
  id: string;
  address: Address;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  /** See {@link ServiceAddressCard}. */
  fieldErrors?: FieldErrors;
  /** See {@link ServiceAddressCard}. Defaults to "serviceAddress". */
  fieldName?: string;
  disabled?: boolean;
}

/**
 * ServiceAddressCardWithMutation is a {@link ServiceAddressCard} saving the
 * address with a PUT of `{ id, serviceAddress }`. Its validation errors are
 * written into the nearest ErrValidationFailedContextProvider, which the page
 * mounts.
 */
export function ServiceAddressCardWithMutation<
  Req extends { id: string; serviceAddress: Address },
  Res,
>({
  id,
  address,
  mutation,
  title = t("Serviceadresse"),
  description = t("Adresse an der der Service durchgeführt werden soll."),
  fieldErrors = undefined,
  fieldName = "serviceAddress",
  disabled = false,
}: ServiceAddressCardWithMutationProps<Req, Res>) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, serviceAddress: address });
  const debouncedMutation = mutation();
  useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, serviceAddress: address },
  );

  return (
    <ServiceAddressCard
      address={request.serviceAddress}
      // @ts-expect-error - I do not understand the error. But it works.
      onAddressChange={(a) => setRequest({ id, serviceAddress: a })}
      title={title}
      description={description}
      fieldErrors={fieldErrors}
      fieldName={fieldName}
      disabled={disabled}
    />
  );
}
